import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Alert, message, Spin, Tour } from 'antd';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import useHomeConfig from 'hooks/useHomeConfig';
import SubscriptionStore from 'store/Subscription';
import { isFreeUserByInfo } from 'utils/provider';
import { Provider, EFeatureType } from 'types/provider';
import { CouponType, EServiceType, EWebinarScheduleType, TKlarityServiceServerTableItem } from 'types/common';
import type { TProfileData } from 'types/home';
import Summary from '../Summary';
import TopOpportunities from '../TopOpportunities';
import GrowYourBusiness from '../GrowYourBusiness';
import QuickActions from '../QuickActions';
import GrowthTips from '../GrowthTips';
import TodayAppointments from '../TodayAppointments';
import TodoList from '../TodoList';
import UnreadMessages from '../UnreadMessages';
import KlarityQuickActions from '../KlarityQuickActions';
import WhatsNewOnKlarity from '../WhatsNewOnKlarity';
import AppointmentTrends from '../AppointmentTrends';
import ProviderStore from 'store/Provider';
import type { TourProps } from 'antd';
import { getQuickAction, getAvailableHoursInNextNHours } from 'api/home';
import TopOpportunitiesGuide from '../TopOpportunitiesGuide';
import s from './s.module.less';
import CareCommitmentCards from 'components/CareCommitmentCards';
import ProviderCampainHeader from 'components/ProviderCampainHeader';
import useGetCampaignList from 'hooks/Campaign/useGetCampaignList';
import CampaignStore from 'store/Campaign';
import useGetCCCList from 'hooks/ccc/useGetCCCList';
import CCCStore from 'store/CCC';
import { ICCCDashboardItem } from 'types/ccc';
import useGetProviderReferralInfo from 'hooks/referral/useGetProviderReferralInfo';
import KiwiReferralBanner from 'components/ProviderCampainHeader/campains/KiwiReferralBanner';
// import useGetTelehealthVisitAndPrescriptionRefill from 'hooks/Campaign/useGetTelehealthVisitAndPrescriptionRefill';
import { ICampaign } from 'types/campaign';
import WebinarBooking from 'components/WebinarBooking';
import { checkIsPaymentFailed } from 'api/ehr';
import WeightLoss15mins from 'components/ProviderCampainHeader/campains/WeightLoss15mins';
import { useNavigate } from 'react-router-dom';
import { DISABLE_PRACTICE_FRONT_BANNER } from 'constants/common';
import { PATH } from 'constants/path';
import ProListingLogo from 'assets/home/prolisting-logo.svg';
import useAdStatus from 'hooks/marketing/useAdStatus';
import dayjs from 'utils/dayjs';
import DateSelect from 'pages/PPAOnboardingFinishPage/components/DateSelect';
import { updateLaunchTime } from 'api/ppaOnboarding';
import { getUrlParam, shouldUseKlarityUi, isPPAOnboardingCompleted } from 'utils/common';
import SetMoreTimeBanner from '../SetMoreTimeBanner';
import StepBanner from 'components/StepBanner';
import AskChartBanner from 'components/AskChartBanner';
import AskChartWelcomeModal from 'components/AskChartWelcomeModal';
import {
    getHasCloseAskChartWelcomeModal,
    setHasCloseAskChartWelcomeModal,
    getHasShowPrescriptionConfigModal,
    setHasShowPrescriptionConfigModal,
} from 'utils/localstore';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import BannerCarousel from '../BannerCarousel';
import commonS from 'styles/common.module.less';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import PrescriptionConfigModal from './PrescriptionConfigModal';
import useHomePopup from 'hooks/useHomePopup';
import { EHomePopupKey } from 'constants/homePopups';
import useCheckLeadFormByEmail from 'hooks/ppa/useCheckLeadFormByEmail';
import { isCredentialingHelpType, hasIntent, EHelpType } from 'pages/PPALeadFormPage/helpType';
import useCurrentUser from 'hooks/useCurrentUser';
import { ERoleCode } from 'types/enumItems';

type TProps = {
    showWebinarPop: boolean;
    showWebinarBanner: boolean;
    webinarLink: string;
    webinarTitle: string;
    webinarDes: string;
    webinarScheduleType?: EWebinarScheduleType;
    onCloseWebinarPop: (booked: boolean) => void;
    onScheduled: () => void;
};

const GrowthHub = (props: TProps) => {
    const {
        showWebinarPop,
        webinarLink,
        webinarTitle,
        webinarDes,
        webinarScheduleType,
        showWebinarBanner,
        onCloseWebinarPop,
        onScheduled,
    } = props;
    const navigate = useNavigate();
    const [data, loadingHome, fetchData] = useProviderHomeInfo();
    const [klarityServiceList, loadingKlarityServiceList, fetchKlarityServiceList] = useProviderKlarityServiceList();
    const [prescriptionServiceList, setPrescriptionServiceList] = useState<TKlarityServiceServerTableItem[]>([]);
    const [get] = ProviderStore.useStore();
    const provider: Provider = get('data') as Provider;
    const [configList, loadingConfig] = useHomeConfig();
    const [, contextHolder] = message.useMessage();
    const payPlan = SubscriptionStore.get('currentPlan');
    const isFreeUser = payPlan && isFreeUserByInfo(payPlan);
    const agreementReviewTypeList = data?.agreementReviewTypeList;
    const inInMeetingWhitelist = true;
    const isKlarity = shouldUseKlarityUi();
    const isPPAProvider = !!data?.ppaProvider;
    //这判断有点乱，因为以前只有ppa和ppv，然后有些渲染只要不是ppa就展示，现在除了ppa和ppv还有别的，同时又增加了一些ppv才能出现的逻辑，ai review时记得思考下背景。。。
    const isPPVProvider = !!data?.ppvProvider;
    const hasCredentialing = !!data?.features?.includes(EFeatureType.KLARITY_CREDENTIALING);
    const allowAddVerifyPlus = true; //!!data?.features?.includes(EFeatureType.KLARITY_VERIFIED_PLUS);
    const [getCampaignStore] = CampaignStore.useStore();
    const [getCCCStore] = CCCStore.useStore();
    const forceRefetchCampaignList = getCampaignStore('forceRefetchCampaignList');
    const forceRefetchCCCList = getCCCStore('forceRefetchCCCList');
    // const [dateRangeFilter, setDateRangeFilter] = useState<string>('LAST_30_DAYS');
    const [cccDashboardData, setCCCDashboardData] = useState<ICCCDashboardItem[]>([]);
    const [campaignList, setCampaignList] = useState<ICampaign[]>([]);
    const [showPaymentTips, setShowPaymentTips] = useState(false);
    const [adStatus] = useAdStatus();
    const [dateSelectOpen, setDateSelectOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [showAskChartWelcomeModal, setShowAskChartWelcomeModal] = useState(!getHasCloseAskChartWelcomeModal());
    const [showGrowthTips, setShowGrowthTips] = useState(false);
    const [isPracticeListingBannerClosed, setIsPracticeListingBannerClosed] = useState(false);

    const spinning = loadingHome || loadingConfig;
    const [profileData, setProfileData] = useState<TProfileData>({} as TProfileData);

    const [campaignListData, loadingCampaign, , refetchCampaignList] = useGetCampaignList();
    // const [telehealthVisitAndPrescriptionRefillInfo, loadTelehealthVisitAndPrescriptionRefillInfo] = useGetTelehealthVisitAndPrescriptionRefill();
    const [cccList, loadingCCC, , refetchCCCList] = useGetCCCList();
    const [showPrescriptionConfigModal, setShowPrescriptionConfigModal] = useState(false);
    const [currentUser] = useCurrentUser();
    // Credentialing 路径注册的用户没有上架 Klarity marketplace，不应该看到 CCC（患者忠诚度产品）入口。
    // /provider/ccc/list 对所有 provider 都会返回 SKU 目录，所以必须在前端按注册路径拦掉。
    // 只在 Klarity 侧查（CCC 本身也只在 isKlarity 下渲染），避免给 Kiwi 用户多发一次 lead-form 请求
    const [leadFormData, leadFormLoading] = useCheckLeadFormByEmail(isKlarity ? provider?.email || '' : '');
    const isCredentialingUser = isCredentialingHelpType(leadFormData?.leadForm?.practiceHelpType);
    const showContinueOnboarding =
        !leadFormLoading &&
        hasIntent(leadFormData?.leadForm?.practiceHelpType, EHelpType.PATIENT_GROWTH) &&
        !isPPAOnboardingCompleted(data);
    // leadForm 未返回前先不渲染，避免 credentialing 用户先闪出 CCC 再消失
    const showCCC = !leadFormLoading && !isCredentialingUser;
    const isOwner = currentUser?.roleCode === ERoleCode.GROUP_OWNER;
    const { shouldShow: shouldShowAskChart } = useHomePopup(
        EHomePopupKey.ASK_CHART_WELCOME,
        showAskChartWelcomeModal && !!data?.firstLoginAt,
    );
    const ENABLE_PRESCRIPTION_CONFIG_MODAL = false;
    const { shouldShow: shouldShowPrescription } = useHomePopup(
        EHomePopupKey.PRESCRIPTION_CONFIG,
        ENABLE_PRESCRIPTION_CONFIG_MODAL && showPrescriptionConfigModal,
    );

    useEffect(() => {
        const hasShowPrescriptionConfigModal = getHasShowPrescriptionConfigModal();
        if (hasShowPrescriptionConfigModal) {
            return;
        }
        if (data && klarityServiceList && klarityServiceList.length > 0) {
            const prescriptionRefillService = klarityServiceList.filter(
                (item) => item.serviceType === EServiceType.PRESCRIPTION_SERVICE,
            );
            if (prescriptionRefillService && prescriptionRefillService.length > 0) {
                setPrescriptionServiceList(prescriptionRefillService);
                setShowPrescriptionConfigModal(true);
            }
        }
    }, [data, klarityServiceList]);

    // 合并telehealthVisitAndPrescriptionRefillInfo，作为campaignList的一部分
    useEffect(() => {
        const listData = [...(campaignListData || [])];
        // 为关闭LA wildfire campaign而注释掉。后面如果还需要用到这个api，请打开
        // 并且确认 LA wildfire campaign 数据是否依然存在。后端目前无法关闭LA wildfire canJoin = false
        // if (telehealthVisitAndPrescriptionRefillInfo && telehealthVisitAndPrescriptionRefillInfo.canJoin) {
        //     // if (telehealthVisitAndPrescriptionRefillInfo) {
        //     listData.push(telehealthVisitAndPrescriptionRefillInfo);
        // }

        setCampaignList([...listData]);
        // }, [telehealthVisitAndPrescriptionRefillInfo, campaignListData]);
    }, [campaignListData]);

    const [providerReferralInfo, referalLoading] = useGetProviderReferralInfo();

    useEffect(() => {
        if (forceRefetchCampaignList) {
            refetchCampaignList();
        }
    }, [forceRefetchCampaignList, refetchCampaignList]);

    useEffect(() => {
        if (forceRefetchCCCList) {
            refetchCCCList();
        }
    }, [forceRefetchCCCList, refetchCCCList]);

    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref3 = useRef(null);
    const ref4 = useRef(null);

    const [open, setOpen] = useState<boolean>(false);
    const { shouldShow: shouldShowHomeTour } = useHomePopup(EHomePopupKey.HOME_TOUR, open);

    const hasAgreement =
        payPlan?.couponType !== CouponType.HEADWAY && agreementReviewTypeList && agreementReviewTypeList.length > 0;

    const getGreeting = () => {
        const currentHour = new Date().getHours();
        let greeting = '';
        if (currentHour >= 5 && currentHour < 12) {
            greeting = 'Good morning';
        } else if (currentHour >= 12 && currentHour < 17) {
            greeting = 'Good afternoon';
        } else if (currentHour >= 17 && currentHour < 21) {
            greeting = 'Good evening';
        } else {
            greeting = 'Hello';
        }

        return greeting;
    };

    const steps: TourProps['steps'] = [
        {
            title: ' ',
            description:
                'Track key practice metrics and assess progress. Use top-right filters to analyze trends over specific periods.',
            target: () => ref1.current,
            placement: 'bottom',
        },
        {
            title: ' ',
            placement: 'top',
            description:
                'Your guide to practice growth. Discover strategies to expand your patient base, enhance experience, and diversify services.',
            target: () => ref2.current,
        },
        {
            title: ' ',
            placement: 'left',
            description:
                'Your practice growth roadmap. Track progress on key objectives with helpful tips and resources.',
            target: () => ref3.current,
        },
        {
            title: ' ',
            placement: 'left',
            description: 'Quick access to frequently used tools and resources, streamlining workflow and saving time.',
            target: () => ref4.current,
        },
        {
            title: ' ',
            placement: 'left',
            description: 'Share your Google profile to existing patients to earn 5-star rating.',
            target: () => ref4.current,
        },
        {
            title: ' ',
            placement: 'left',
            description: 'Share your Practice Front to allow patients to find out your full services',
            target: () => ref4.current,
        },
    ];

    const getData = useCallback(async () => {
        const result = await getQuickAction();
        setProfileData(result.data.data);
        if (!localStorage.getItem('HOME_TOUR_ISOPEN') && !isKlarity) {
            setOpen(true);
        }
    }, [isKlarity]);

    const paymentTipRender = async () => {
        try {
            const { data: res } = await checkIsPaymentFailed();
            setShowPaymentTips(res?.data);
        } catch (e) {}
    };

    useEffect(() => {
        getData();
        paymentTipRender();
    }, [getData]);

    // 检测URL参数，如果包含action=openGolive则自动打开弹窗
    useEffect(() => {
        getUrlParam('action');
        const action = getUrlParam('action');
        if (action === 'openGolive' && isPPAProvider && data?.expectLaunchAt && !data?.klarityListed) {
            setDateSelectOpen(true);
        }
    }, [isPPAProvider, data?.expectLaunchAt, data?.klarityListed]);

    // 获取可用小时数，决定是否显示GrowthTips
    useEffect(() => {
        const fetchAvailableHours = async () => {
            // 只在PPA Provider且Klarity用户时检查
            if (isPPAProvider && isKlarity) {
                try {
                    const result = await getAvailableHoursInNextNHours(168);
                    if (!result.error && result.data && typeof result.data.data === 'number') {
                        // 如果返回的data小于等于30，则显示GrowthTips
                        const availableHours = result.data.data;
                        setShowGrowthTips(availableHours <= 30);
                    } else {
                        // API调用失败时，默认不显示
                        setShowGrowthTips(false);
                    }
                } catch (error) {
                    // 错误处理，默认不显示
                    setShowGrowthTips(false);
                }
            } else {
                // 非PPA Provider或非Klarity用户，不显示
                setShowGrowthTips(false);
            }
        };

        fetchAvailableHours();
    }, [isPPAProvider, isKlarity]);

    const handleSetCCCDashboardData = (value: ICCCDashboardItem[]) => {
        setCCCDashboardData(value);
    };

    const handleDateSelectCancel = () => {
        setDateSelectOpen(false);
    };

    const handleDateConfirm = async (selectedDate: dayjs.Dayjs) => {
        setConfirmLoading(true);
        try {
            const res = await updateLaunchTime(selectedDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'));
            if (!res.error) {
                setDateSelectOpen(false);
                message.success(`Date updated to ${selectedDate.format('MMM D, YYYY')}`);
                // 刷新页面数据以获取最新的日期
                fetchData();
            } else {
                message.error('Failed to update date');
            }
        } catch (error) {
            message.error('Failed to update date');
        } finally {
            setConfirmLoading(false);
        }
    };

    const campaignBannerRender = useMemo(() => {
        if (isKlarity && campaignList && campaignList.length > 0) {
            return <ProviderCampainHeader campains={campaignList} />;
        }
        if (!isKlarity && providerReferralInfo) {
            return <KiwiReferralBanner referralInfo={providerReferralInfo} />;
        }
    }, [campaignList, isKlarity, providerReferralInfo]);

    const practiceFrontBannerRender = useMemo(() => {
        if (!localStorage.getItem(DISABLE_PRACTICE_FRONT_BANNER)) {
            const handleClick = () => {
                localStorage.setItem(DISABLE_PRACTICE_FRONT_BANNER, 'true');
                navigate('/dashboard/marketing?mainTab=external&subTab=practice-front');
            };
            return (
                <Alert
                    className={s.practiceFrontBanner}
                    message="We've moved your Practice Front page to External marketing. You can now view and manage it under the Marketing tab."
                    type="info"
                    showIcon
                    action={
                        <div className={s.practiceFrontButton} onClick={handleClick}>
                            View
                        </div>
                    }
                />
            );
        }
    }, [navigate]);

    const profileLiveDateBannerRender = useMemo(() => {
        const expectLaunchAt = data?.expectLaunchAt;
        if (isPPAProvider && expectLaunchAt && !data?.klarityListed) {
            const handleClick = () => {
                setDateSelectOpen(true);
            };
            const bannerConfig = [
                {
                    title: `Your profile is expected to go live on ${dayjs(expectLaunchAt).format(
                        'MMM D, YYYY',
                    )}. Want to start earlier?`,
                    btnText: 'Change date',
                    btnClick: handleClick,
                },
                {
                    title: 'Set up your no-show policy and get paid',
                    desc: 'Add policies for no-shows, late cancellations, and rescheduling to reduce missed appointments and keep your schedule on track.',
                    btnText: 'Set up now',
                    btnClick: () => navigate(`/${PATH.PAYMENT_SETUP}`),
                },
            ];
            return <StepBanner bannerConfig={bannerConfig} type="info" />;
        }
    }, [data?.expectLaunchAt, data?.klarityListed, isPPAProvider]);

    const practiceListingBannerRender = useMemo(() => {
        if (adStatus.hasJoinedAdSystem || isPracticeListingBannerClosed) {
            return null;
        }
        return (
            <div className={s.practiceListingBanner}>
                <img src={ProListingLogo} alt="Pro-Listings" className={s.practiceListingBannerLogo} />
                <div className={s.practiceListingBannerContentWrap}>
                    <div className={s.practiceListingBannerTitle}>
                        🚀 Grow your patient panel faster with highly-visible Pro-Listings
                    </div>
                    <div className={s.practiceListingBannerContent}>
                        Get priority placement in patient search results. An ads solution that enhance your visibility
                        and fast track your patient bookings.
                    </div>
                </div>
                <div
                    className={s.practiceListingBannerButton}
                    onClick={() => navigate(`${PATH.DASHBOARD}/${PATH.MARKETING}?tab=prolisting`)}
                >
                    See details
                </div>
                <span onClick={() => setIsPracticeListingBannerClosed(true)} className={s.practiceListingBannerClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                            d="M1 13L13 1M1 1L13 13"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </div>
        );
    }, [adStatus.hasJoinedAdSystem, navigate, isPracticeListingBannerClosed]);

    const renderPPVorKiwiContent = () => {
        return (
            <>
                {isKlarity ? (
                    <BannerCarousel
                        showAskChartBanner
                        showZoomVideoVisitsBanner={inInMeetingWhitelist}
                        practiceListingBanner={practiceListingBannerRender}
                        showPracticeListingBanner={!adStatus.hasJoinedAdSystem}
                        onPracticeListingBannerClose={() => setIsPracticeListingBannerClosed(true)}
                    />
                ) : (
                    practiceListingBannerRender
                )}
                <div className={s.content}>
                    <div className={s.left}>
                        <div style={{ width: '100%' }} ref={ref1}>
                            <Summary
                                showWebinarBanner={showWebinarBanner}
                                webinarTitle={webinarTitle}
                                webinarDes={webinarDes}
                                webinarLink={webinarLink}
                                webinarScheduleType={webinarScheduleType}
                                onScheduled={onScheduled}
                                isKlarity={isKlarity}
                                profileData={profileData}
                                setCCCDashboardData={handleSetCCCDashboardData}
                            />
                        </div>
                        {data?.ppvProvider && showCCC && cccList && cccList.length > 0 && (
                            <CareCommitmentCards cccList={cccList} cccDashboardData={cccDashboardData} />
                        )}

                        {isKlarity && <TodayAppointments />}
                        {isPPVProvider && (
                            <div>
                                {open ? (
                                    <div ref={ref2} className={s.positionStep2}>
                                        <TopOpportunitiesGuide isKlarity={isKlarity} campaignList={campaignListData} />
                                    </div>
                                ) : (
                                    <TopOpportunities isKlarity={isKlarity} campaignList={campaignListData} />
                                )}
                            </div>
                        )}
                    </div>
                    <div className={s.right}>
                        {isKlarity && (
                            <>
                                <TodoList />
                                <UnreadMessages />
                            </>
                        )}
                        <div ref={ref3}>
                            <GrowYourBusiness isKlarity={isKlarity} />
                        </div>
                        <div ref={ref4}>
                            <QuickActions profileData={profileData} isKlarity={isKlarity} homeInfo={data} />
                        </div>
                    </div>
                </div>
            </>
        );
    };
    return (
        <Spin tip="Loading" size="large" spinning={spinning || loadingCampaign || loadingCCC || referalLoading}>
            <div className={s.wrapper}>
                <div className={s.inner}>
                    {contextHolder}
                    <div className={s.header}>
                        <div className={s.title}>
                            {getGreeting()}
                            {provider?.firstName ? `, ${provider?.firstName}` : ''} 👋🏻
                        </div>
                    </div>
                    {data?.proListing && <SetMoreTimeBanner />}

                    {profileLiveDateBannerRender}
                    {/* {(isPpvProvider || !isKlarity) && practiceFrontBannerRender} */}
                    {/* {campaignBannerRender} */}
                    {/* {isPpvProvider && <WeightLoss15mins />} */}
                    {/* {(hasAgreement || showWebinarBanner || showPaymentTips) && (
                        <div className={s.bannerWrap}>
                            <CombinedBanner
                                fetchData={fetchData}
                                webinarTitle={webinarTitle}
                                webinarDes={webinarDes}
                                webinarLink={webinarLink}
                                webinarScheduleType={webinarScheduleType}
                                // klarity 用户需要展示agreement, 其他用户不需要展示
                                hasAgreement={hasAgreement && isKlarity}
                                showPaymentTips={showPaymentTips}
                            />
                        </div>
                    )} */}
                    {(isPPAProvider || !isPPVProvider) && (
                        <>
                            <BannerCarousel
                                //showJoinVerifyPlus
                                showJoinVerifyPlus={!!isOwner && allowAddVerifyPlus}
                                showAskChartBanner
                                showZoomVideoVisitsBanner={inInMeetingWhitelist}
                                practiceListingBanner={practiceListingBannerRender}
                                showPayLaterBanner={false}
                                showPracticeListingBanner={!adStatus.hasJoinedAdSystem}
                                onPracticeListingBannerClose={() => setIsPracticeListingBannerClosed(true)}
                            />
                            <Summary
                                showWebinarBanner={showWebinarBanner}
                                webinarTitle={webinarTitle}
                                webinarDes={webinarDes}
                                webinarLink={webinarLink}
                                webinarScheduleType={webinarScheduleType}
                                onScheduled={onScheduled}
                                isKlarity={isKlarity}
                                profileData={profileData}
                                setCCCDashboardData={handleSetCCCDashboardData}
                            />
                            {showGrowthTips && <GrowthTips />}
                            <div className={s.klarityContent}>
                                <div className={s.klarityLeft}>
                                    <TodayAppointments />
                                    {/* <InProgressTextVisits /> */}
                                    <AppointmentTrends />
                                </div>
                                <div className={s.klarityRight}>
                                    <TodoList />
                                    <UnreadMessages />
                                    <KlarityQuickActions showContinueOnboarding={false} />
                                    <WhatsNewOnKlarity />
                                </div>
                            </div>
                        </>
                    )}
                    {isPPVProvider && renderPPVorKiwiContent()}
                </div>
            </div>
            <Tour
                open={shouldShowHomeTour}
                onClose={() => {
                    setOpen(false);
                    localStorage.setItem('HOME_TOUR_ISOPEN', 'true');
                }}
                steps={steps}
                indicatorsRender={(current, total) => (
                    <span>
                        Step {current + 1} out of {total}
                    </span>
                )}
            />
            <WebinarBooking
                modalHeader={
                    <div className={s.wHeader}>
                        <div className={s.wTitle}>
                            <span className={s.wHigh}>!!</span>
                            Don&apos;t miss out:
                            {webinarTitle?.replace('Schedule', 'schedule')}
                        </div>
                        <div className={s.wDes}>{webinarDes}</div>
                    </div>
                }
                show={false}
                link={webinarLink}
                webinarScheduleType={webinarScheduleType}
                onClose={onCloseWebinarPop}
            />
            <DateSelect
                open={dateSelectOpen}
                onCancel={handleDateSelectCancel}
                onConfirm={handleDateConfirm}
                confirmLoading={confirmLoading}
            />
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_HOME} defaultShow={false} makeFix={false} />
            </div>
            <PrescriptionConfigModal
                open={shouldShowPrescription}
                onClose={() => {
                    setShowPrescriptionConfigModal(false);
                    setHasShowPrescriptionConfigModal(true);
                }}
                onConfirm={() => {
                    setShowPrescriptionConfigModal(false);
                    setHasShowPrescriptionConfigModal(true);
                }}
                prescriptionServiceList={prescriptionServiceList}
            />
            <AskChartWelcomeModal
                open={shouldShowAskChart}
                onClose={() => {
                    setShowAskChartWelcomeModal(false);
                    setHasCloseAskChartWelcomeModal(true);
                }}
            />
        </Spin>
    );
};

export default GrowthHub;
